import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { supabase } from '../integrations/supabase/client';
import { FichaCaptacao } from '../types/ficha-captacao';
import { Trash2, Edit, Eye, Plus } from 'lucide-react';
import { formatCurrency } from '../utils/masks';

interface FichaCaptacaoListProps {
  onEdit: (ficha: FichaCaptacao) => void;
  onView: (ficha: FichaCaptacao) => void;
  onNew: () => void;
}

export const FichaCaptacaoList: React.FC<FichaCaptacaoListProps> = ({
  onEdit,
  onView,
  onNew
}) => {
  const { toast } = useToast();
  const [fichas, setFichas] = useState<FichaCaptacao[]>([]);
  const [loading, setLoading] = useState(true);

  const loadFichas = async () => {
    try {
      const { data, error } = await supabase
        .from('fichas_captacao')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFichas(data as FichaCaptacao[] || []);
    } catch (error) {
      console.error('Erro ao carregar fichas:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar as fichas de captação.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFichas();
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta ficha?')) return;

    try {
      const { error } = await supabase
        .from('fichas_captacao')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Ficha excluída com sucesso."
      });

      await loadFichas();
    } catch (error) {
      console.error('Erro ao excluir ficha:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir a ficha.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Carregando fichas...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Fichas de Captação</h1>
        <Button onClick={onNew}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Ficha
        </Button>
      </div>

      {fichas.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Nenhuma ficha de captação encontrada.</p>
          <Button onClick={onNew} className="mt-4">
            <Plus className="h-4 w-4 mr-2" />
            Criar primeira ficha
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {fichas.map((ficha) => (
            <Card key={ficha.id} className="p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold">{ficha.proprietario_nome}</h3>
                    <p className="text-sm text-muted-foreground">{ficha.proprietario_telefone}</p>
                  </div>
                  <Badge variant={ficha.status === 'definitivo' ? 'default' : 'secondary'}>
                    {ficha.status}
                  </Badge>
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">{ficha.imovel_tipo}</p>
                  <p className="text-sm text-muted-foreground">
                    {ficha.imovel_endereco}, {ficha.imovel_bairro}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {ficha.imovel_cidade} - {ficha.imovel_estado}
                  </p>
                </div>

                <div className="space-y-1">
                  {(ficha.valor_venda || 0) > 0 && (
                    <p className="text-sm">
                      <span className="font-medium">Venda:</span> {formatCurrency(ficha.valor_venda || 0)}
                    </p>
                  )}
                  {(ficha.valor_locacao || 0) > 0 && (
                    <p className="text-sm">
                      <span className="font-medium">Locação:</span> {formatCurrency(ficha.valor_locacao || 0)}
                    </p>
                  )}
                </div>

                <div className="text-xs text-muted-foreground">
                  Criado em {new Date(ficha.created_at).toLocaleDateString('pt-BR')}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onView(ficha)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(ficha)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(ficha.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};